import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchNightAuditPreview, closeNightAudit, getRole, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { printTableReport } from "../utils/printDoc";
import { useT } from "../i18n/useT";

// Front Desk — Phase A5: the night audit. A lean operational close: the
// system-verified end-of-day checks, cash count / variance, operational
// movement counts, the credit carried forward to the next business day, and a
// one-tap "close day" that releases overdue 6PM holds as no-shows.
const S = {
  en: {
    title: "Night audit", today: "Business day", back: "Back to board",
    rooms_sold: "Rooms sold",
    movements: "Movements", arrivals: "Arrivals (expected / arrived)", departures: "Departures (due / done)",
    stay_overs: "Stay-overs", holds: "6PM holds pending",
    close: "Close the day",
    closed_ok: "Day closed.", released: "marked no-show",
    no_show_due: "Un-arrived → no-show",
    checklist_title: "System checks",
    chk_tabs: "F&B tabs closed", chk_tabs_bad: "{n} open",
    chk_tills: "Cashier tills closed", chk_tills_bad: "{n} open",
    chk_dep: "Departures checked out", chk_dep_bad: "{n} due",
    chk_noshow: "No pending no-shows", chk_noshow_bad: "{n} pending (released on close)",
    chk_missing: "All occupied rooms have a charge", chk_missing_bad: "{n} occupied room(s) with no room charge",
    chk_posted: "Revenue posted (rooms, F&B, services)",
    chk_auto_tag: "automatic", view: "view →",
    manual_title: "Manual confirmations", manual_tag: "manual",
    m_payments: "Payments reconciled (cash / Telebirr / CBE / card counted against the system)",
    m_fiscal: "Fiscal Z printed / filed",
    m_housekeeping: "Housekeeping board set for tomorrow",
    m_handover: "Incidents / handover recorded",
    m_reports: "Reports printed & secured",
    cash_expected_l: "Cash expected (system)", cash_counted_l: "Cash counted", cash_variance_l: "Variance",
    cash_title: "Cash count",
    handover_l: "Handover note to next shift", handover_ph: "Anything the morning team should know…",
    var_reason_l: "Reason for cash variance (required)", var_reason_ph: "Explain the over / short…",
    need_var_reason: "Please enter a reason for the cash variance before closing.",
    unchecked_confirm: "Some items are not complete:", close_anyway: "Close the day anyway?",
    closed_badge: "Day closed",
    dr_credit_fwd: "Credit carried forward to {date}",
    print: "Print day-close sheet", ok_word: "OK", pending_word: "PENDING", yes_word: "Yes", no_word: "No",
    sheet_checks: "Checks", sheet_signoff: "Signed (duty manager)",
  },
  am: {
    title: "የሌሊት ኦዲት", today: "የስራ ቀን", back: "ወደ ዳሽቦርድ ተመለስ",
    rooms_sold: "የተሸጡ ክፍሎች",
    movements: "እንቅስቃሴዎች", arrivals: "መግቢያ (የሚጠበቅ / የገባ)", departures: "መውጫ (የሚጠበቅ / የወጣ)",
    stay_overs: "ቆይታዎች", holds: "6PM ማቆያ በመጠባበቅ",
    close: "ቀኑን ዝጋ",
    closed_ok: "ቀን ተዘግቷል።", released: "አልመጣም ተመዝግቧል",
    no_show_due: "ያልመጡ → አልመጣም",
    checklist_title: "System checks", // TODO verify
    chk_tabs: "F&B tabs closed", chk_tabs_bad: "{n} open", // TODO verify
    chk_tills: "Cashier tills closed", chk_tills_bad: "{n} open", // TODO verify
    chk_dep: "Departures checked out", chk_dep_bad: "{n} due", // TODO verify
    chk_noshow: "No pending no-shows", chk_noshow_bad: "{n} pending (released on close)", // TODO verify
    chk_missing: "All occupied rooms have a charge", chk_missing_bad: "{n} occupied room(s) with no room charge", // TODO verify
    chk_posted: "Revenue posted (rooms, F&B, services)", // TODO verify
    chk_auto_tag: "automatic", view: "view →", // TODO verify
    manual_title: "Manual confirmations", manual_tag: "manual", // TODO verify
    m_payments: "Payments reconciled (cash / Telebirr / CBE / card counted against the system)", // TODO verify
    m_fiscal: "Fiscal Z printed / filed", // TODO verify
    m_housekeeping: "Housekeeping board set for tomorrow", // TODO verify
    m_handover: "Incidents / handover recorded", // TODO verify
    m_reports: "Reports printed & secured", // TODO verify
    cash_expected_l: "የሚጠበቅ ጥሬ ገንዘብ (ሲስተም)", cash_counted_l: "የተቆጠረ ጥሬ ገንዘብ", cash_variance_l: "ልዩነት",
    cash_title: "Cash count", // TODO verify
    handover_l: "ለቀጣዩ ፈረቃ የርክክብ ማስታወሻ", handover_ph: "የጠዋቱ ቡድን ማወቅ ያለበት…",
    var_reason_l: "የጥሬ ገንዘብ ልዩነት ምክንያት (ግዴታ)", var_reason_ph: "ብልጫ/ጉድለቱን ያብራሩ…",
    need_var_reason: "ከመዝጋትዎ በፊት የጥሬ ገንዘብ ልዩነቱን ምክንያት ያስገቡ።",
    unchecked_confirm: "Some items are not complete:", close_anyway: "Close the day anyway?", // TODO verify
    closed_badge: "ቀን ተዘግቷል",
    dr_credit_fwd: "ወደ {date} የተላለፈ ብድር",
    print: "የቀን መዝጊያ ወረቀት አትም", ok_word: "እሺ", pending_word: "በመጠባበቅ ላይ", yes_word: "አዎ", no_word: "አይ",
    sheet_checks: "ማረጋገጫዎች", sheet_signoff: "የፈረመ (ተረኛ ኃላፊ)",
  },
  om: {
    title: "Odiitii halkanii", today: "Guyyaa hojii", back: "Gara gabatee deebi'i",
    rooms_sold: "Kutaa gurgurame",
    movements: "Sochiiwwan", arrivals: "Ga'umsa (eegame / ga'e)", departures: "Ba'umsa (eegame / xumurame)",
    stay_overs: "Turtii dabalataa", holds: "Eeggannoo 6PM eegaa jiran",
    close: "Guyyaa cufi",
    closed_ok: "Guyyaan cufame.", released: "akka no-show galmaa'e",
    no_show_due: "Hin dhufne → no-show",
    checklist_title: "System checks", // TODO verify
    chk_tabs: "F&B tabs closed", chk_tabs_bad: "{n} open", // TODO verify
    chk_tills: "Cashier tills closed", chk_tills_bad: "{n} open", // TODO verify
    chk_dep: "Departures checked out", chk_dep_bad: "{n} due", // TODO verify
    chk_noshow: "No pending no-shows", chk_noshow_bad: "{n} pending (released on close)", // TODO verify
    chk_missing: "All occupied rooms have a charge", chk_missing_bad: "{n} occupied room(s) with no room charge", // TODO verify
    chk_posted: "Revenue posted (rooms, F&B, services)", // TODO verify
    chk_auto_tag: "automatic", view: "view →", // TODO verify
    manual_title: "Manual confirmations", manual_tag: "manual", // TODO verify
    m_payments: "Payments reconciled (cash / Telebirr / CBE / card counted against the system)", // TODO verify
    m_fiscal: "Fiscal Z printed / filed", // TODO verify
    m_housekeeping: "Housekeeping board set for tomorrow", // TODO verify
    m_handover: "Incidents / handover recorded", // TODO verify
    m_reports: "Reports printed & secured", // TODO verify
    cash_expected_l: "Maallaqa eegame (sistemii)", cash_counted_l: "Maallaqa lakkaa'ame", cash_variance_l: "Garaagarummaa",
    cash_title: "Cash count", // TODO verify
    handover_l: "Yaadannoo dabarsaa gara sochii itti aanuutti", handover_ph: "Wanti gareen ganamaa beekuu qabu…",
    var_reason_l: "Sababa garaagarummaa maallaqaa (dirqama)", var_reason_ph: "Dabalataa / hir'ina ibsi…",
    need_var_reason: "Guyyaa cufuun dura sababa garaagarummaa maallaqaa galchi.",
    unchecked_confirm: "Some items are not complete:", close_anyway: "Close the day anyway?", // TODO verify
    closed_badge: "Guyyaan cufame",
    dr_credit_fwd: "Liqii gara {date} dabarfame",
    print: "Waraqaa cufaa guyyaa maxxansi", ok_word: "OK", pending_word: "EEGAA", yes_word: "Eeyyee", no_word: "Lakki",
    sheet_checks: "Sakatta'a", sheet_signoff: "Mallatteesse (itti gaafatamaa)",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const TILE = (accent) => ({ ...CARD, padding: "14px 16px", flex: "1 1 150px", minWidth: 140, borderTop: `3px solid ${accent}` });

// Manual confirmations: the few end-of-day steps the system can't verify on its
// own, so a human ticks them. Stored verbatim in the close payload's checklist.
const MANUAL = ["m_payments", "m_fiscal", "m_housekeeping", "m_handover", "m_reports"];


export default function NightAuditPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const role = getRole();
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  const [cashCounted, setCashCounted] = useState("");
  const [varReason, setVarReason] = useState("");
  const [handover, setHandover] = useState("");
  // Manual confirmations — a human ticks each; the system does not verify them.
  const [checks, setChecks] = useState({ m_payments: false, m_fiscal: false, m_housekeeping: false, m_handover: false, m_reports: false });

  const load = () => fetchNightAuditPreview().then(setD).catch(() => setD(null));
  useEffect(() => { load(); }, []);

  // Pre-fill from a saved close (so reopening the day shows what was recorded).
  useEffect(() => {
    if (d && d.saved) {
      if (d.saved.cash_counted != null) setCashCounted(String(d.saved.cash_counted));
      if (d.saved.cash_variance_reason) setVarReason(d.saved.cash_variance_reason);
      if (d.saved.handover_note) setHandover(d.saved.handover_note);
      if (d.saved.checklist) setChecks((s) => ({ ...s, ...MANUAL.reduce((o, k) => (d.saved.checklist[k] != null ? { ...o, [k]: !!d.saved.checklist[k] } : o), {}) }));
    }
  }, [d]);  // eslint-disable-line react-hooks/exhaustive-deps

  const cashExpected = (d && d.cash_expected) || 0;
  const variance = cashCounted === "" ? null : Number(cashCounted) - cashExpected;

  // System-verified checks derived from the night-audit data — these reflect
  // real system state (not manually ticked boxes), so a green check means the
  // system itself confirmed the condition. Each failing row links to where the
  // manager resolves it (open tabs, open tills, due departures, pending
  // no-shows). The revenue row is always ok: ODRA posts revenue as-you-go and
  // the nightly room accrual runs on close, so it's marked "automatic" with no
  // link rather than something a human must action.
  const autoChecks = d ? [
    { key: "tabs", ok: (d.open_tabs || 0) === 0, bad: t("chk_tabs_bad").replace("{n}", d.open_tabs || 0), label: t("chk_tabs"), to: "/cashier" },
    { key: "tills", ok: (d.tills_open || 0) === 0, bad: t("chk_tills_bad").replace("{n}", d.tills_open || 0), label: t("chk_tills"), to: "/tills" },
    { key: "departures", ok: (d.departures_due || 0) === 0, bad: t("chk_dep_bad").replace("{n}", d.departures_due || 0), label: t("chk_dep"), to: "/front-desk/board" },
    { key: "noshow", ok: (d.no_show_due || 0) === 0, bad: t("chk_noshow_bad").replace("{n}", d.no_show_due || 0), label: t("chk_noshow"), to: "/front-desk/board" },
    { key: "missing", ok: (d.rooms_missing_posting_count || 0) === 0, bad: t("chk_missing_bad").replace("{n}", d.rooms_missing_posting_count || 0), label: t("chk_missing"), to: "/reservations" },
    { key: "posted", ok: true, auto: true, label: t("chk_posted") },
  ] : [];

  const doPrint = () => {
    if (!d) return;
    const m = (v) => formatPrice(v || 0, cur);
    const movements = {
      heading: t("movements"),
      headers: [t("movements"), t("today")],
      rows: [
        [`${t("rooms_sold")} / ${d.rooms_total}`, d.rooms_sold],
        [t("arrivals"), `${d.arrivals_expected} / ${d.arrived}`],
        [t("departures"), `${d.departures_due} / ${d.departed}`],
        [t("stay_overs"), d.stay_overs],
        [t("holds"), d.holds_pending],
        [t("no_show_due"), d.no_show_due],
        Object.assign([t("dr_credit_fwd").replace("{date}", d.next_business_date), m(d.credit_carried_forward)], { bold: true }),
      ],
    };
    const cash = {
      heading: t("cash_title"),
      headers: [t("cash_title"), ""],
      rows: [
        [t("cash_expected_l"), m(cashExpected)],
        [t("cash_counted_l"), cashCounted === "" ? "—" : m(Number(cashCounted))],
        Object.assign([t("cash_variance_l"), variance == null ? "—" : m(variance)], { bold: true }),
        ...(varReason.trim() ? [[t("var_reason_l"), varReason.trim()]] : []),
      ],
    };
    const checksSec = {
      heading: t("sheet_checks"),
      headers: [t("checklist_title"), ""],
      rows: [
        ...autoChecks.map((c) => [c.label, c.ok ? t("ok_word") : (c.bad || t("pending_word"))]),
        ...MANUAL.map((k) => [t(k), checks[k] ? t("yes_word") : t("no_word")]),
      ],
    };
    const sections = [movements, cash, checksSec];
    if (handover.trim()) sections.push({ heading: t("handover_l"), headers: [t("handover_l"), ""], rows: [[handover.trim(), ""]] });
    sections.push({ headers: [`${t("sheet_signoff")}: ____________________`, ""], rows: [] });
    printTableReport({ title: t("title"), subtitle: `${t("today")}: ${d.date}`, sections });
  };

  const close = async () => {
    // Soft guard: a cash variance must always be explained.
    if (variance != null && Math.abs(variance) >= 0.005 && !varReason.trim()) {
      window.alert(t("need_var_reason"));
      return;
    }
    // Warn-and-confirm (N1): list any failing system check (with its live
    // detail) and any un-ticked manual confirmation, then require an explicit
    // confirmation. The always-ok "automatic" revenue row never blocks. The
    // business day must always be able to close, so this warns but never blocks.
    const failingAuto = autoChecks.filter((c) => !c.ok && !c.auto);
    const missingManual = MANUAL.filter((k) => !checks[k]);
    if (failingAuto.length > 0 || missingManual.length > 0) {
      const lines = [
        ...failingAuto.map((c) => "• " + c.label + " — " + c.bad),
        ...missingManual.map((k) => "• " + t(k)),
      ];
      if (!window.confirm(t("unchecked_confirm") + "\n" + lines.join("\n") + "\n\n" + t("close_anyway"))) return;
    }
    try {
      const res = await closeNightAudit({
        // Record what was true at the moment of close: the ticked manual
        // confirmations plus what the system verified.
        checklist: {
          m_payments: checks.m_payments,
          m_fiscal: checks.m_fiscal,
          m_housekeeping: checks.m_housekeeping,
          m_handover: checks.m_handover,
          m_reports: checks.m_reports,
          tabs_closed: autoChecks[0].ok,
          tills_closed: autoChecks[1].ok,
          departures_done: autoChecks[2].ok,
          noshow_clear: autoChecks[3].ok,
        },
        cash_counted: cashCounted === "" ? null : Number(cashCounted),
        cash_variance_reason: varReason || null,
        handover_note: handover || null,
      });
      setD(res);
      setMsg(`${t("closed_ok")} ${res.no_shows_released || 0} ${t("released")}.`);
    } catch { load(); }
  };

  const Tile = ({ label, value, accent }) => (
    <div style={TILE(accent)}>
      <div style={{ fontSize: 24, fontWeight: 800, color: accent }}>{value}</div>
      <div className="muted" style={{ fontSize: 13 }}>{label}</div>
    </div>
  );
  const Row = ({ label, value, strong }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #f2f5f5", fontWeight: strong ? 700 : 400 }}>
      <span className={strong ? "" : "muted"} style={{ fontSize: 13.5 }}>{label}</span>
      <span style={{ fontSize: 13.5 }}>{value}</span>
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Link className="muted" to="/front-desk/board" style={{ fontSize: 13, textDecoration: "none" }}>← {t("back")}</Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="muted" style={{ fontSize: 14 }}>{t("today")} · {d ? d.date : "…"}</span>
          <button className="btn secondary small" disabled={!d} onClick={doPrint}>🖨 {t("print")}</button>
        </span>
      </div>

      {d == null ? <p className="muted" style={{ marginTop: 14 }}>…</p> : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
            <Tile label={`${t("rooms_sold")} / ${d.rooms_total}`} value={d.rooms_sold} accent="#063B48" />
            <Tile label={t("stay_overs")} value={d.stay_overs} accent="#2563A8" />
            <Tile label={t("no_show_due")} value={d.no_show_due} accent="#A32D2D" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginTop: 14 }}>
            <div style={{ ...CARD, padding: 14 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("movements")}</div>
              <Row label={t("arrivals")} value={`${d.arrivals_expected} / ${d.arrived}`} />
              <Row label={t("departures")} value={`${d.departures_due} / ${d.departed}`} />
              <Row label={t("stay_overs")} value={d.stay_overs} />
              <Row label={t("holds")} value={d.holds_pending} />
              <Row label={t("no_show_due")} value={d.no_show_due} />
            </div>
          </div>

          {/* Operational point of the night audit: the unpaid balance carried
              forward to the next business day. */}
          <div style={{ marginTop: 14, background: "#FDF3E7", border: "1px solid #F0D5AE", color: "#8A5A12", borderRadius: 12, padding: "10px 14px", fontWeight: 700, fontSize: 14 }}>
            {t("dr_credit_fwd").replace("{date}", d.next_business_date)}: {formatPrice(d.credit_carried_forward || 0, cur)}
          </div>

          {/* System-verified close checks: computed from live data, not ticked
              by hand — a red row is a real, outstanding condition. */}
          <div style={{ ...CARD, padding: 14, marginTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontWeight: 700 }}>{t("checklist_title")}</div>
              {d.saved && d.saved.closed_at && (
                <span style={{ background: "#E7F3EE", color: "#0F6E56", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
                  ✓ {t("closed_badge")} · {new Date(d.saved.closed_at).toLocaleString()}
                </span>
              )}
            </div>
            <div style={{ marginTop: 8 }}>
              {autoChecks.map((c) => {
                const rowStyle = { display: "flex", gap: 8, alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f2f5f5" };
                const inner = (
                  <>
                    <span style={{ fontSize: 15, fontWeight: 800, color: c.ok ? "#1D9E75" : "#A32D2D", width: 16, textAlign: "center" }}>{c.ok ? "✓" : "✗"}</span>
                    <span style={{ fontSize: 14 }}>{c.label}</span>
                    {c.auto && (
                      <span style={{ background: "#EAF1FA", color: "#2563A8", borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{t("chk_auto_tag")}</span>
                    )}
                    {!c.ok && (
                      <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#A32D2D" }}>{c.bad}</span>
                        <span style={{ fontSize: 12, color: "#5f6b78" }}>{t("view")}</span>
                      </span>
                    )}
                  </>
                );
                // A failing check links to where the manager resolves it; a
                // passing (green ✓) or always-on "automatic" check is a plain,
                // non-clickable row.
                return c.ok ? (
                  <div key={c.key} style={rowStyle}>{inner}</div>
                ) : (
                  <Link key={c.key} to={c.to} style={{ ...rowStyle, textDecoration: "none", color: "inherit" }}>{inner}</Link>
                );
              })}
            </div>

            {/* Manual confirmations: the steps the system can't verify, so a
                human ticks each. Clearly tagged "manual" to set them apart from
                the system-verified rows above. */}
            <div style={{ fontWeight: 700, marginTop: 14, marginBottom: 4 }}>{t("manual_title")}</div>
            <div>
              {MANUAL.map((k) => (
                <label key={k} style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f2f5f5", fontSize: 14, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!checks[k]} onChange={(e) => setChecks((s) => ({ ...s, [k]: e.target.checked }))} />
                  <span>{t(k)}</span>
                  <span style={{ marginLeft: "auto", background: "#f2f5f5", color: "#5f6b78", borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{t("manual_tag")}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Cash count / variance — the one figure the system can't verify on
              its own, so it stays a manual count with a required reason. */}
          <div style={{ ...CARD, padding: 14, marginTop: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("cash_title")}</div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div><div className="muted" style={{ fontSize: 12 }}>{t("cash_expected_l")}</div><div style={{ fontWeight: 700 }}>{formatPrice(cashExpected, cur)}</div></div>
              <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cash_counted_l")}<br />
                <input type="number" min="0" step="0.01" value={cashCounted} onChange={(e) => setCashCounted(e.target.value)} style={{ width: 120, textAlign: "right" }} />
              </label>
              <div><div className="muted" style={{ fontSize: 12 }}>{t("cash_variance_l")}</div>
                <div style={{ fontWeight: 700, color: variance == null ? "#5f6b78" : Math.abs(variance) < 0.005 ? "#1D9E75" : "#A32D2D" }}>{variance == null ? "—" : formatPrice(variance, cur)}</div>
              </div>
              {variance != null && Math.abs(variance) >= 0.005 && (
                <label style={{ flexBasis: "100%", fontSize: 12, color: "#A32D2D" }}>{t("var_reason_l")}<br />
                  <input value={varReason} onChange={(e) => setVarReason(e.target.value)} placeholder={t("var_reason_ph")} maxLength={500} style={{ width: "100%", maxWidth: 440, fontFamily: "inherit" }} />
                </label>
              )}
            </div>
            <div style={{ marginTop: 12 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 3 }}>{t("handover_l")}</div>
              <textarea value={handover} onChange={(e) => setHandover(e.target.value)} rows={2} placeholder={t("handover_ph")} maxLength={2000} style={{ width: "100%", resize: "vertical", fontFamily: "inherit" }} />
            </div>
          </div>

          {role === "manager" && (
            <div style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <button className="btn primary" onClick={close}>{t("close")}</button>
              {msg && <span style={{ color: "#1D7A44", fontSize: 14 }}>{msg}</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
